import { useEffect, useState } from "react";
import api, { formatApiError, formatMoney, todayLocalIso } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { XCircle, FileSpreadsheet, FileText } from "lucide-react";
import { exportToExcel, exportToPdf } from "@/lib/exportUtils";

export default function PedidosCancelados() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const today = todayLocalIso();
  const firstDay = today.slice(0, 8) + "01";
  const [range, setRange] = useState({ desde: firstDay, hasta: today });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/reports/pedidos-cancelados?desde=${range.desde}&hasta=${range.hasta}`);
      setItems(data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const rows = items.map(o => ({
    numero: o.numero,
    fecha_pedido: o.fecha?.slice(0, 10),
    fecha_anulacion: o.anulado_at?.slice(0, 10),
    vendedor: o.creado_por,
    cliente: o.cliente_nombre,
    total: o.total,
    anticipo: o.anticipo,
    motivo: o.motivo_anulacion,
    anulado_por: o.anulado_por,
  }));

  const columns = [
    { key: "numero", label: "N° Pedido" },
    { key: "fecha_pedido", label: "Fecha pedido", type: "date" },
    { key: "fecha_anulacion", label: "Fecha anulación", type: "date" },
    { key: "vendedor", label: "Vendedor" },
    { key: "cliente", label: "Cliente" },
    { key: "total", label: "Total", type: "money" },
    { key: "anticipo", label: "Anticipo", type: "money" },
    { key: "motivo", label: "Motivo" },
    { key: "anulado_por", label: "Anulado por" },
  ];

  return (
    <div className="space-y-6" data-testid="pedidos-cancelados-page">
      <header>
        <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 font-semibold">Reportes</div>
        <h1 className="font-manrope font-black text-4xl mt-1 flex items-center gap-3"><XCircle className="w-8 h-8 text-red-600" /> Pedidos Cancelados</h1>
        <p className="text-zinc-500 text-sm mt-2">No se cuentan como ventas activas ni CxC. Registros anulados con motivo y auditoría.</p>
      </header>

      <Card className="p-4 border-zinc-200">
        <div className="flex flex-wrap items-end gap-3">
          <div><Label>Desde</Label><Input type="date" value={range.desde} onChange={(e) => setRange({ ...range, desde: e.target.value })} className="w-40" /></div>
          <div><Label>Hasta</Label><Input type="date" value={range.hasta} onChange={(e) => setRange({ ...range, hasta: e.target.value })} className="w-40" /></div>
          <Button onClick={load} className="bg-[#002FA7] hover:bg-[#00227A]">Buscar</Button>
          <div className="flex-1"></div>
          <Button variant="outline" onClick={() => exportToExcel({ title: "Pedidos cancelados", filters: range, columns, rows, filename: `cancelados_${range.desde}_${range.hasta}` })} className="gap-2"><FileSpreadsheet className="w-4 h-4" />Excel</Button>
          <Button variant="outline" onClick={() => exportToPdf({ title: "Pedidos cancelados", filters: range, columns, rows, filename: `cancelados_${range.desde}_${range.hasta}` })} className="gap-2"><FileText className="w-4 h-4" />PDF</Button>
        </div>
      </Card>

      <Card className="border-zinc-200 overflow-hidden">
        <div className="p-4 border-b border-zinc-100 flex items-center justify-between">
          <div className="font-semibold">{items.length} pedidos anulados</div>
          <div className="text-right">
            <div className="text-[10px] uppercase text-zinc-500 font-semibold">Valor anulado</div>
            <div className="font-black text-2xl text-red-600 tabular-nums">{formatMoney(items.reduce((s, x) => s + Number(x.total || 0), 0))}</div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-zinc-600">
              <tr className="text-left">
                <th className="px-3 py-2 text-[10px] uppercase font-semibold">Pedido</th>
                <th className="px-3 py-2 text-[10px] uppercase font-semibold">Fecha</th>
                <th className="px-3 py-2 text-[10px] uppercase font-semibold">Anulado</th>
                <th className="px-3 py-2 text-[10px] uppercase font-semibold">Cliente</th>
                <th className="px-3 py-2 text-[10px] uppercase font-semibold">Vendedor</th>
                <th className="px-3 py-2 text-[10px] uppercase font-semibold text-right">Total</th>
                <th className="px-3 py-2 text-[10px] uppercase font-semibold">Motivo</th>
                <th className="px-3 py-2 text-[10px] uppercase font-semibold">Anulado por</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {loading && <tr><td colSpan={8} className="text-center text-zinc-400 py-8">Cargando…</td></tr>}
              {!loading && items.length === 0 && <tr><td colSpan={8} className="text-center text-zinc-400 py-10">Sin pedidos cancelados en el periodo.</td></tr>}
              {items.map((o) => (
                <tr key={o.id} className="hover:bg-zinc-50">
                  <td className="px-3 py-2"><Link to={`/pedidos/${o.id}`} className="font-mono text-xs font-semibold text-blue-700 hover:underline">{o.numero}</Link></td>
                  <td className="px-3 py-2 text-xs">{o.fecha?.slice(0, 10)}</td>
                  <td className="px-3 py-2 text-xs">{o.anulado_at?.slice(0, 10)}</td>
                  <td className="px-3 py-2 text-xs">{o.cliente_nombre}</td>
                  <td className="px-3 py-2 text-xs">{o.creado_por}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold text-red-600">{formatMoney(o.total)}</td>
                  <td className="px-3 py-2 text-xs">{o.motivo_anulacion}</td>
                  <td className="px-3 py-2 text-xs">{o.anulado_por}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
